import { languageMapping } from './constants/languageCodes'

let initialized = false
let requestedLanguage = null
let activeTranslationWaitCleanup = null
const initialTranslationPendingClass = 'google-translation-pending'
const routeTranslationPendingClass = 'google-translation-route-pending'
const googleTranslateScriptId = 'google-translate-script'

const getCookieLanguage = () => {
  const match = document.cookie.match(/googtrans=\/ru\/(\w+)/)
  return match?.[1] ?? null
}

const finishGoogleTranslationLoadingByClass = (pendingClass) => {
  document.documentElement.classList.remove(pendingClass)
  window.clearTimeout(window.googleTranslationLoadingTimeout)
}

export const finishGoogleTranslationLoading = () => {
  finishGoogleTranslationLoadingByClass(initialTranslationPendingClass)
}

const startGoogleTranslationLoading = (pendingClass) => {
  document.documentElement.classList.add(pendingClass)
  window.clearTimeout(window.googleTranslationLoadingTimeout)
  window.googleTranslationLoadingTimeout = window.setTimeout(
    () => finishGoogleTranslationLoadingByClass(pendingClass),
    8000,
  )
}

const waitForGoogleTranslation = (
  language,
  pendingClass = initialTranslationPendingClass,
) => {
  activeTranslationWaitCleanup?.()
  activeTranslationWaitCleanup = null

  if (language === 'ru') {
    finishGoogleTranslationLoadingByClass(pendingClass)
    return
  }

  let revealTimeout = null
  let targetLanguageMutationDetected = false

  const cleanup = () => {
    window.clearInterval(checkLanguageInterval)
    window.clearTimeout(revealTimeout)
    observer.disconnect()

    if (activeTranslationWaitCleanup === cleanup) {
      activeTranslationWaitCleanup = null
    }
  }

  const isTranslationApplied = () =>
    document.documentElement.classList.contains('translated-ltr') ||
    document.documentElement.classList.contains('translated-rtl') ||
    document.body?.classList.contains('translated-ltr') ||
    document.body?.classList.contains('translated-rtl')

  const checkTranslation = (isDomMutation = false) => {
    const isPending = document.documentElement.classList.contains(
      pendingClass,
    )

    if (!isPending) {
      cleanup()
      return
    }

    const select = document.querySelector('.goog-te-combo')

    if (!select) return

    if (select.value !== language) {
      window.clearTimeout(revealTimeout)
      revealTimeout = null
      targetLanguageMutationDetected = false
      select.value = language
      select.dispatchEvent(new Event('change'))
      return
    }

    if (isDomMutation && isTranslationApplied()) {
      targetLanguageMutationDetected = true
      window.clearTimeout(revealTimeout)
      revealTimeout = null
    }

    if (
      targetLanguageMutationDetected &&
      isTranslationApplied() &&
      !revealTimeout
    ) {
      revealTimeout = window.setTimeout(() => {
        const currentSelect = document.querySelector('.goog-te-combo')

        if (currentSelect?.value !== language) {
          revealTimeout = null
          targetLanguageMutationDetected = false
          return
        }

        cleanup()
        finishGoogleTranslationLoadingByClass(pendingClass)
      }, 400)
    }
  }

  const observer = new MutationObserver(() => checkTranslation(true))
  const checkLanguageInterval = window.setInterval(checkTranslation, 100)
  const routeContent = document.querySelector(
    '.app-wrapper .page-content:not([data-google-translation-loader])',
  )
  const observationTarget =
    pendingClass === routeTranslationPendingClass && routeContent
      ? routeContent
      : document.documentElement
  const observerOptions =
    pendingClass === routeTranslationPendingClass
      ? {
          childList: true,
          characterData: true,
          subtree: true,
        }
      : {
          attributes: true,
          attributeFilter: ['class'],
          childList: true,
          characterData: true,
          subtree: true,
        }

  observer.observe(observationTarget, observerOptions)
  activeTranslationWaitCleanup = cleanup
  checkTranslation()
}

const applyRequestedLanguage = () => {
  if (!requestedLanguage || requestedLanguage === 'ru') return

  const select = document.querySelector('.goog-te-combo')

  if (select && select.value !== requestedLanguage) {
    select.value = requestedLanguage
    select.dispatchEvent(new Event('change'))
  }
}

const isGoogleTranslateAvailable = () =>
  typeof window.google?.translate?.TranslateElement === 'function'

const handleGoogleTranslateInitializationError = () => {
  initialized = false
  document.getElementById(googleTranslateScriptId)?.remove()
  finishGoogleTranslationLoading()
}

export const initGoogleTranslate = (languageCodes, initialLanguage = null) => {
  requestedLanguage = initialLanguage || requestedLanguage

  if (initialized) {
    applyRequestedLanguage()
    return
  }

  initialized = true

  const elementId = 'google_translate_element'

  if (!document.getElementById(elementId)) {
    const element = document.createElement('div')
    element.id = elementId
    element.style.display = 'none'
    document.body.appendChild(element)
  }

  const init = () => {
    if (!isGoogleTranslateAvailable()) {
      handleGoogleTranslateInitializationError()
      return
    }

    try {
      new window.google.translate.TranslateElement(
        {
          pageLanguage: 'ru',
          includedLanguages: languageCodes.join(','),
          autoDisplay: false,
        },
        elementId,
      )
      window.setTimeout(applyRequestedLanguage, 0)
    } catch {
      handleGoogleTranslateInitializationError()
    }
  }

  if (!isGoogleTranslateAvailable()) {
    const script = document.createElement('script')
    script.id = googleTranslateScriptId
    script.src =
      '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit'

    window.googleTranslateElementInit = init
    script.onerror = () => {
      handleGoogleTranslateInitializationError()
    }

    document.head.appendChild(script)
  } else {
    init()
  }
}

export const prepareInitialGoogleTranslation = (languages) => {
  const activeLanguages = (Array.isArray(languages) ? languages : [])
    .filter(
      (language) =>
        Number(language?.is_active) === 1 && languageMapping[language.id],
    )
    .sort((a, b) => a.rank - b.rank)

  if (!activeLanguages.length) {
    finishGoogleTranslationLoading()
    return
  }

  const cookieLanguage = getCookieLanguage()
  let selectedLanguageId = null

  try {
    const savedLanguageId = localStorage.getItem('selectedLanguage')
    selectedLanguageId = savedLanguageId === null ? null : Number(savedLanguageId)
  } catch {
    selectedLanguageId = null
  }

  const cookieLanguageData = activeLanguages.find(
    (language) => languageMapping[language.id].code === cookieLanguage,
  )
  const savedLanguageData = activeLanguages.find(
    (language) => Number(language.id) === selectedLanguageId,
  )
  const russianLanguageData = activeLanguages.find(
    (language) => languageMapping[language.id].code === 'ru',
  )
  const initialLanguageData =
    savedLanguageData ||
    cookieLanguageData ||
    russianLanguageData ||
    activeLanguages[0]
  const initialLanguage = languageMapping[initialLanguageData.id].code
  const languageCodes = activeLanguages.map(
    (language) => languageMapping[language.id].code,
  )

  const shouldSaveLanguageCookie =
    initialLanguage !== cookieLanguage &&
    !(initialLanguage === 'ru' && !cookieLanguage)

  if (shouldSaveLanguageCookie) {
    changeGoogleLanguage(initialLanguage)
  }

  waitForGoogleTranslation(initialLanguage)
  initGoogleTranslate(languageCodes, initialLanguage)
}

export const prepareGoogleTranslationForRoute = () => {
  if (!requestedLanguage || requestedLanguage === 'ru') return

  const pendingClass = document.querySelector('.app-wrapper')
    ? routeTranslationPendingClass
    : initialTranslationPendingClass

  if (
    document.documentElement.classList.contains(initialTranslationPendingClass)
  ) {
    return
  }

  startGoogleTranslationLoading(pendingClass)
  waitForGoogleTranslation(requestedLanguage, pendingClass)
}

export const changeGoogleLanguage = (lang) => {
  requestedLanguage = lang

  const hostname = window.location.hostname
  const domainParts = hostname.split('.')
  const rootDomain = domainParts.slice(-2).join('.')
  const withSubdomain = '.' + rootDomain

  document.cookie = `googtrans=/ru/${lang}; path=/;`
  document.cookie = `googtrans=/ru/${lang}; path=/; domain=${withSubdomain};`

  if (lang === 'ru') {
    activeTranslationWaitCleanup?.()
    activeTranslationWaitCleanup = null
    finishGoogleTranslationLoadingByClass(initialTranslationPendingClass)
    finishGoogleTranslationLoadingByClass(routeTranslationPendingClass)
    return
  }

  const select = document.querySelector('.goog-te-combo')

  if (select) {
    select.value = lang
    select.dispatchEvent(new Event('change'))
  }

  if (
    document.documentElement.classList.contains(routeTranslationPendingClass)
  ) {
    waitForGoogleTranslation(lang, routeTranslationPendingClass)
  }
}
